import { supabase } from '../supabaseClient';
import { readFunctionError } from '../utils/functionError';
import {
  hasPermission,
  isSystemAdministrator,
  toStaffRoleRefs,
  type StaffAccess,
  type StaffRoleRef,
  type StaffTier,
} from './roleService';

/**
 * The tier somebody's roles add up to, as the database summarises it on their
 * profile. The roles themselves are on `StaffRow.roles`.
 */
export type StaffRole = StaffTier;
export type StaffStatus = 'active' | 'disabled';
export type StaffInviteStatus = 'pending' | 'expired' | 'accepted';

export const STAFF_ROLES: StaffRole[] = ['guard', 'manager', 'admin'];

/** Weekly hours a guard is contracted for. A null top is a casual contract with nothing guaranteed. */
export interface ContractHoursBand {
  minHours: number;
  maxHours: number | null;
}

export type EroMinimumHoursExceptionKind = 'operational_hours_below_24' | 'subsisting_arrangement';

export interface EroMinimumHoursException {
  kind: EroMinimumHoursExceptionKind;
  note: string;
}

/** Mirrors public.contract_hour_bands(); set_guard_contract_hours refuses anything else. */
export const CONTRACT_HOUR_BANDS: ContractHoursBand[] = [
  { minHours: 0, maxHours: null },
  ...[5, 10, 15, 20].map((minHours) => ({ minHours, maxHours: minHours + 5 })),
  { minHours: 24, maxHours: 25 },
  ...[25, 30, 35, 40, 45].map((minHours) => ({ minHours, maxHours: minHours + 5 })),
];

/** A stable value for a band, for a <select>. */
export const contractBandKey = (band: ContractHoursBand): string => (
  band.maxHours === null ? `${band.minHours}+` : `${band.minHours}-${band.maxHours}`
);

export const findContractBand = (key: string): ContractHoursBand | null => (
  CONTRACT_HOUR_BANDS.find((band) => contractBandKey(band) === key) ?? null
);

/**
 * One thing a guard is bound to. A client means every site of that contract,
 * including sites added to it later; a site means that site.
 */
export interface SiteBinding {
  kind: 'client' | 'site';
  id: string;
  name: string;
  /** A bound site's client, to tell apart two sites with one name. */
  clientName: string | null;
  /** The client, or the bound site's client, is archived: nothing there is being rostered. */
  archived: boolean;
}

/** What set_guard_site_bindings takes. Two empty lists mean every site. */
export interface SiteBindingSelection {
  clientContractIds: string[];
  siteIds: string[];
}

export interface BindingClientOption {
  id: string;
  name: string;
  archived: boolean;
}

export interface BindingSiteOption {
  id: string;
  name: string;
  clientContractId: string | null;
}

/** Every client and site a guard could be bound to. */
export interface SiteBindingOptions {
  clients: BindingClientOption[];
  sites: BindingSiteOption[];
}

export interface StaffDetailsInput {
  firstName: string;
  lastName: string;
  /** As typed; only the digits are kept. Empty leaves them without one. */
  phone: string;
}

/**
 * A request to move the email somebody signs in with, waiting on them. Nobody
 * changes another person's sign-in: staff ask, a link goes to the new address,
 * and the person confirms it with their own password.
 */
export interface EmailReset {
  /** Where the link went. Not their sign-in until they confirm. */
  email: string;
  requestedAt: string | null;
  /** Null when the link could not be handed to the mail provider. */
  lastSentAt: string | null;
  expiresAt: string | null;
  /** Run out, or locked by wrong passwords: only sending again will do. */
  expired: boolean;
}

export interface EmailResetRequestResult {
  email: string;
  expiresAt: string;
  /** False for somebody invited who has never signed in: the link alone confirms, and takes them on to set-up. */
  requiresPassword: boolean;
}

/** Something the workspace needs to know about a person that nobody has told it yet. */
export type MissingProfileDetail = 'contract_hours';

export interface StaffRow {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  role: StaffRole;
  /** Every role they hold: at most one guard role, any number of management roles. */
  roles: StaffRoleRef[];
  /** Holds a guard role, whatever else they hold. A guard who also manages has role 'manager'. */
  is_guard: boolean;
  /** The System Owner: the one administrator the company ultimately belongs to. */
  is_owner: boolean;
  status: StaffStatus;
  created_at: string | null;
  company_country_code?: string | null;
  ero_minimum_hours_exception_kind?: EroMinimumHoursExceptionKind | null;
  ero_minimum_hours_exception_note?: string | null;
  /** null for someone who joined with a join code and was never invited by email. */
  invite_status: StaffInviteStatus | null;
  invite_expires_at: string | null;
  invite_last_sent_at: string | null;
  /** Null until somebody records it; a guard who joined with a join code starts without one. */
  contract_hours: ContractHoursBand | null;
  /** The clients and sites a guard is bound to. Empty means every site, which is the default. */
  site_bindings: SiteBinding[];
  /** An email reset they have not confirmed yet. `email` above is still what they sign in with. */
  email_reset: EmailReset | null;
}

export type StaffLicenceStatus = 'pending_review' | 'approved' | 'rejected' | 'expired' | 'revoked';
export type StaffLicenceAuthority = 'PSA' | 'SIA' | 'OTHER';

export interface StaffLicenceRow {
  id: string;
  guard_id: string;
  licence_number: string;
  licence_type_id: string | null;
  licence_type_name: string | null;
  authority: StaffLicenceAuthority;
  expiry_date: string;
  status: StaffLicenceStatus;
  submitted_at: string;
}

export interface InviteStaffParams {
  firstName: string;
  lastName: string;
  email: string;
  /** At least one. At most one guard role. */
  roleIds: string[];
}

export interface StaffInviteResult {
  userId: string;
  email: string;
  role: StaffRole;
  expiresAt: string;
  /** False when the account was created but the email could not be handed to the mail provider. */
  emailSent: boolean;
}

/**
 * What is missing from somebody's profile. Only people who work shifts have
 * contract hours, and a deactivated account is nobody's to-do.
 */
export const missingProfileDetails = (
  row: Pick<StaffRow, 'is_guard' | 'status' | 'contract_hours'>,
): MissingProfileDetail[] => (
  row.is_guard && row.status === 'active' && row.contract_hours === null ? ['contract_hours'] : []
);

/**
 * The ERO entitlement starts after six months' service. The staff join date is
 * currently the service-date proxy used by the product.
 */
export const hasIrishEroMinimumHoursIssue = (
  row: Pick<StaffRow,
    | 'is_guard'
    | 'status'
    | 'created_at'
    | 'company_country_code'
    | 'contract_hours'
    | 'ero_minimum_hours_exception_kind'
    | 'ero_minimum_hours_exception_note'
  >,
  asOf = new Date(),
): boolean => {
  if (!row.is_guard || row.status !== 'active') return false;
  if (row.company_country_code?.trim().toUpperCase() !== 'IE' || !row.created_at) return false;
  if (row.contract_hours && row.contract_hours.minHours >= 24) return false;

  const serviceStart = new Date(row.created_at);
  if (Number.isNaN(serviceStart.getTime())) return false;
  serviceStart.setUTCMonth(serviceStart.getUTCMonth() + 6);
  if (serviceStart > asOf) return false;

  return !row.ero_minimum_hours_exception_kind
    || (row.ero_minimum_hours_exception_note?.trim().length ?? 0) < 10;
};

/** Invited by email and not yet through the link: on the roster, but unable to sign in. */
export const isAwaitingSetup = (row: Pick<StaffRow, 'invite_status'>): boolean => (
  row.invite_status === 'pending' || row.invite_status === 'expired'
);

type Actor = Pick<StaffAccess, 'tier' | 'permissions'>;

/**
 * Whether the actor may deactivate or restore this person. Mirrors
 * set_staff_account_status, which is what actually decides: it takes the staff
 * permission, and anybody with workspace access is a system administrator's
 * to switch off.
 */
export const canChangeAccountStatus = (
  actor: Actor,
  actorId: string | null,
  target: Pick<StaffRow, 'id' | 'role'>,
): boolean => {
  if (!actorId || actorId === target.id) return false;
  if (!hasPermission(actor, 'staff')) return false;
  return isSystemAdministrator(actor) || target.role === 'guard';
};

/** Whether the actor may send this person's invite again. Mirrors refresh_staff_invite. */
export const canResendInvite = (actor: Actor, target: Pick<StaffRow, 'role'>): boolean => (
  hasPermission(actor, 'staff') && (isSystemAdministrator(actor) || target.role === 'guard')
);

/**
 * Whether the actor may correct this person's name and phone number, and ask
 * for their email to be reset. Mirrors update_staff_profile_details and
 * request_staff_email_reset, which draw the same line: somebody with
 * workspace access is a system administrator's to change. The System Owner is
 * no exception for the email, because asking moves nothing; only their own
 * password confirms it.
 */
export const canEditDetailsOf = (actor: Actor, target: Pick<StaffRow, 'role'>): boolean => (
  hasPermission(actor, 'staff') && (isSystemAdministrator(actor) || target.role === 'guard')
);

/** "Mary Anne Byrne" as the two fields the rest of the system keeps: "Mary", "Anne Byrne". */
export const splitFullName = (fullName: string | null | undefined): { firstName: string; lastName: string } => {
  const [firstName = '', ...rest] = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  return { firstName, lastName: rest.join(' ') };
};

/** What a guard's bindings are as the lists set_guard_site_bindings takes. */
export const toBindingSelection = (bindings: SiteBinding[]): SiteBindingSelection => ({
  clientContractIds: bindings.filter((binding) => binding.kind === 'client').map((binding) => binding.id),
  siteIds: bindings.filter((binding) => binding.kind === 'site').map((binding) => binding.id),
});

const toRole = (value: unknown): StaffRole => (
  value === 'admin' || value === 'manager' ? value : 'guard'
);

const toInviteStatus = (value: unknown): StaffInviteStatus | null => (
  value === 'pending' || value === 'expired' || value === 'accepted' ? value : null
);

function toContractHours(row: Record<string, unknown>): ContractHoursBand | null {
  const minHours = row.contract_min_weekly_hours;
  if (typeof minHours !== 'number') return null;
  const maxHours = row.contract_max_weekly_hours;
  return { minHours, maxHours: typeof maxHours === 'number' ? maxHours : null };
}

/** Absent from a database that predates bindings, which reads as bound to nothing. */
function toSiteBindings(value: unknown): SiteBinding[] {
  if (!Array.isArray(value)) return [];
  return (value as Record<string, unknown>[]).flatMap((binding) => {
    if (typeof binding?.id !== 'string') return [];
    return [{
      kind: binding.kind === 'client' ? 'client' as const : 'site' as const,
      id: binding.id,
      name: (binding.name as string | null) ?? '',
      clientName: (binding.client_name as string | null) ?? null,
      archived: binding.archived === true,
    }];
  });
}

function toEmailReset(value: unknown): EmailReset | null {
  if (!value || typeof value !== 'object') return null;
  const reset = value as Record<string, unknown>;
  if (typeof reset.email !== 'string' || !reset.email) return null;
  return {
    email: reset.email,
    requestedAt: (reset.requested_at as string | null) ?? null,
    lastSentAt: (reset.last_sent_at as string | null) ?? null,
    expiresAt: (reset.expires_at as string | null) ?? null,
    expired: reset.expired === true,
  };
}

function toStaffRow(row: Record<string, unknown>): StaffRow {
  const role = toRole(row.role);

  return {
    id: row.id as string,
    full_name: (row.full_name as string | null) ?? null,
    email: (row.email as string | null) ?? null,
    phone: row.phone != null ? String(row.phone) : null,
    avatar_url: (row.avatar_url as string | null) ?? null,
    role,
    roles: toStaffRoleRefs(row.roles),
    is_guard: typeof row.is_guard === 'boolean' ? row.is_guard : role === 'guard',
    is_owner: row.is_owner === true,
    status: row.status === 'disabled' ? 'disabled' : 'active',
    created_at: (row.created_at as string | null) ?? null,
    company_country_code: (row.company_country_code as string | null) ?? null,
    ero_minimum_hours_exception_kind: (
      row.ero_minimum_hours_exception_kind === 'operational_hours_below_24'
      || row.ero_minimum_hours_exception_kind === 'subsisting_arrangement'
        ? row.ero_minimum_hours_exception_kind
        : null
    ),
    ero_minimum_hours_exception_note: (row.ero_minimum_hours_exception_note as string | null) ?? null,
    invite_status: toInviteStatus(row.invite_status),
    invite_expires_at: (row.invite_expires_at as string | null) ?? null,
    invite_last_sent_at: (row.invite_last_sent_at as string | null) ?? null,
    contract_hours: toContractHours(row),
    site_bindings: toSiteBindings(row.site_bindings),
    email_reset: toEmailReset(row.email_reset),
  };
}

function toLicenceRow(row: Record<string, unknown>): StaffLicenceRow {
  // A joined row can arrive as an object or a one-element array.
  const licenceType = row.licence_type as { name: string | null } | Array<{ name: string | null }> | null;
  return {
    id: row.id as string,
    guard_id: row.guard_id as string,
    licence_number: row.licence_number as string,
    licence_type_id: (row.licence_type_id as string | null) ?? null,
    licence_type_name: Array.isArray(licenceType)
      ? licenceType[0]?.name ?? null
      : licenceType?.name ?? null,
    authority: row.authority as StaffLicenceAuthority,
    expiry_date: row.expiry_date as string,
    status: row.status as StaffLicenceStatus,
    submitted_at: row.submitted_at as string,
  };
}

async function invokeStaffInvites(
  body: Record<string, unknown>,
  fallbackMessage: string,
): Promise<StaffInviteResult> {
  const { data, error } = await supabase.functions.invoke('staff-invites', { body });

  if (error) {
    throw new Error((await readFunctionError(error)).message ?? fallbackMessage);
  }

  const result = data as Partial<StaffInviteResult> | null;
  if (!result?.userId) throw new Error(fallbackMessage);

  return {
    userId: result.userId,
    email: result.email ?? '',
    role: toRole(result.role),
    expiresAt: result.expiresAt ?? '',
    emailSent: result.emailSent !== false,
  };
}

export const staffService = {
  /** Everyone in the company. The RPC is the only read that carries emails and invite state. */
  async fetchStaff(): Promise<StaffRow[]> {
    const { data, error } = await supabase.rpc('get_company_staff');
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(toStaffRow);
  },

  async fetchStaffMember(userId: string): Promise<StaffRow | null> {
    const { data, error } = await supabase.rpc('get_company_staff', { p_user_id: userId });
    if (error) throw error;
    const rows = (data ?? []) as Record<string, unknown>[];
    return rows[0] ? toStaffRow(rows[0]) : null;
  },

  async fetchLicences(guardId: string): Promise<StaffLicenceRow[]> {
    const { data, error } = await supabase
      .from('guard_licences')
      .select(`
        id,
        guard_id,
        licence_number,
        licence_type_id,
        authority,
        expiry_date,
        status,
        submitted_at,
        licence_type:licence_types!guard_licences_licence_type_id_fkey(name)
      `)
      .eq('guard_id', guardId)
      .order('expiry_date', { ascending: true });

    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(toLicenceRow);
  },

  async getCurrentUserId(): Promise<string | null> {
    const { data } = await supabase.auth.getUser();
    return data.user?.id ?? null;
  },

  /**
   * Creates the account and emails the invite. The person is on the roster as
   * soon as this resolves, whether or not they have opened the email.
   */
  async inviteStaff(params: InviteStaffParams): Promise<StaffInviteResult> {
    return invokeStaffInvites(
      {
        action: 'create',
        firstName: params.firstName,
        lastName: params.lastName,
        email: params.email,
        roleIds: params.roleIds,
      },
      'Failed to send invite',
    );
  },

  async resendInvite(userId: string): Promise<StaffInviteResult> {
    return invokeStaffInvites({ action: 'resend', userId }, 'Failed to send invite');
  },

  /** Takes the staff permission, and the band must be one of CONTRACT_HOUR_BANDS. */
  async setContractHours(
    userId: string,
    band: ContractHoursBand,
    eroException: EroMinimumHoursException | null = null,
  ): Promise<void> {
    const { error } = await supabase.rpc('set_guard_contract_hours', {
      p_guard_id: userId,
      p_min_hours: band.minHours,
      p_max_hours: band.maxHours,
      p_ero_exception_kind: eroException?.kind ?? null,
      p_ero_exception_note: eroException?.note.trim() || null,
    });
    if (error) throw error;
  },

  /** Every client and site in the company, by name, for choosing where a guard works. */
  async fetchSiteBindingOptions(): Promise<SiteBindingOptions> {
    const { data, error } = await supabase.rpc('get_site_binding_options');
    if (error) throw error;

    const record = (data ?? {}) as Record<string, unknown>;
    const list = (value: unknown): Record<string, unknown>[] => (
      Array.isArray(value) ? value as Record<string, unknown>[] : []
    );
    return {
      clients: list(record.clients).map((client) => ({
        id: client.id as string,
        name: (client.name as string | null) ?? '',
        archived: client.archived === true,
      })),
      sites: list(record.sites).map((site) => ({
        id: site.id as string,
        name: (site.name as string | null) ?? '',
        clientContractId: (site.client_contract_id as string | null) ?? null,
      })),
    };
  },

  /**
   * Replaces what a guard is bound to; two empty lists put them back to every
   * site. Takes the staff permission. Shifts they already hold are not moved.
   */
  async setSiteBindings(userId: string, selection: SiteBindingSelection): Promise<void> {
    const { error } = await supabase.rpc('set_guard_site_bindings', {
      p_guard_id: userId,
      p_client_contract_ids: selection.clientContractIds,
      p_site_ids: selection.siteIds,
    });
    if (error) throw error;
  },

  /** Corrects a name and phone number. Who may change whom is decided by the database. */
  async updateDetails(userId: string, details: StaffDetailsInput): Promise<void> {
    const { error } = await supabase.rpc('update_staff_profile_details', {
      p_user_id: userId,
      p_first_name: details.firstName.trim(),
      p_last_name: details.lastName.trim(),
      p_phone: details.phone.trim() || null,
    });
    if (error) throw error;
  },

  /**
   * Asks for the email somebody signs in with to be changed. Nothing changes
   * here: a link goes to the new address, and their sign-in moves only when
   * they open it and confirm with their current password. Asking again
   * replaces the link already out.
   */
  async requestEmailReset(userId: string, email: string): Promise<EmailResetRequestResult> {
    const fallbackMessage = 'Could not send the reset link';
    const { data, error } = await supabase.functions.invoke('staff-invites', {
      body: { action: 'request-email-reset', userId, email: email.trim() },
    });

    if (error) {
      throw new Error((await readFunctionError(error)).message ?? fallbackMessage);
    }

    const result = data as Partial<EmailResetRequestResult> | null;
    if (!result?.email) throw new Error(fallbackMessage);

    return {
      email: result.email,
      expiresAt: result.expiresAt ?? '',
      requiresPassword: result.requiresPassword !== false,
    };
  },

  /** Withdraws a reset that has not been confirmed. Its link stops working. */
  async cancelEmailReset(userId: string): Promise<void> {
    const { error } = await supabase.rpc('cancel_staff_email_reset', { p_user_id: userId });
    if (error) throw error;
  },

  async setAccountStatus(userId: string, status: StaffStatus): Promise<void> {
    const { error } = await supabase.rpc('set_staff_account_status', {
      p_user_id: userId,
      p_status: status,
    });
    if (error) throw error;
  },

  getAvatarUrl(avatarPath: string | null | undefined): string | null {
    if (!avatarPath) return null;
    if (avatarPath.startsWith('http')) return avatarPath;
    const { data } = supabase.storage.from('avatars').getPublicUrl(avatarPath);
    return data.publicUrl;
  },
};
